<template>
    <div class="my-5">
        <div class="max-w-2xl">
            <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 class="text-base font-semibold text-gray-900 dark:text-white">CRM Bonus</h2>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Cashback ao comprador, pago pelo produtor a cada venda.
                    </p>
                </div>
                <span
                    v-if="configured && !loading"
                    class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                    :class="status.classes"
                >
                    <span class="h-1.5 w-1.5 rounded-full" :class="status.dot"></span>
                    {{ status.label }}
                </span>
            </div>

            <div
                v-if="loading"
                class="mt-4 flex justify-center rounded-lg border border-gray-200 bg-white py-10 dark:border-gray-700 dark:bg-gray-800"
            >
                <SvgLoader />
            </div>

            <div
                v-else-if="!configured"
                class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-500/30 dark:bg-amber-500/10"
            >
                <p class="text-sm font-medium text-amber-800 dark:text-amber-200">O serviço ainda não existe neste ambiente.</p>
                <p class="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    Rode a migration <code class="font-mono">add_crm_bonus_service_row</code> (package-migrations 1.4.35) e recarregue a página.
                </p>
            </div>

            <form
                v-else
                class="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                @submit.prevent="submit"
            >
                <div class="border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
                    <h3 class="text-sm font-medium text-gray-900 dark:text-white">Cobrança</h3>
                </div>

                <div class="divide-y divide-gray-100 dark:divide-gray-700/50">
                    <div class="flex items-start justify-between gap-6 px-5 py-4">
                        <div>
                            <p class="text-sm text-gray-900 dark:text-white">Cobrança ativa</p>
                            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                Desligada, nenhuma venda nova é cobrada ou registrada. Vendas já cobradas mantêm a taxa.
                            </p>
                        </div>
                        <form-toggle v-model="isActive" :loading="saving" class="!mb-0 shrink-0" />
                    </div>

                    <div class="px-5 py-4" :class="{ 'opacity-60': !isActive }">
                        <div class="flex items-start justify-between gap-6">
                            <div>
                                <label for="crm-bonus-price" class="text-sm text-gray-900 dark:text-white">Preço por venda</label>
                                <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    Sai da comissão do produtor e volta se a venda for reembolsada.
                                </p>
                            </div>
                            <div
                                class="flex w-36 shrink-0 items-center rounded-md border bg-white focus-within:ring-2 focus-within:ring-gray-900 dark:bg-gray-900 dark:focus-within:ring-white"
                                :class="priceError ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'"
                            >
                                <span class="pl-3 text-sm text-gray-500 dark:text-gray-400">R$</span>
                                <input
                                    id="crm-bonus-price"
                                    v-model="priceInput"
                                    type="text"
                                    inputmode="decimal"
                                    autocomplete="off"
                                    class="w-full border-0 bg-transparent py-2 pl-2 pr-3 text-right text-sm tabular-nums text-gray-900 focus:ring-0 dark:text-white"
                                    :aria-invalid="!!priceError"
                                    aria-describedby="crm-bonus-price-hint"
                                    @blur="normalizePrice"
                                />
                            </div>
                        </div>

                        <p id="crm-bonus-price-hint" class="mt-3 text-xs" :class="priceError ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'">
                            <template v-if="priceError">{{ priceError }}</template>
                            <template v-else-if="priceCents === 0">
                                Com R$ 0,00 a venda só fica registrada. Nada sai da comissão.
                            </template>
                            <template v-else>
                                Numa comissão de <span class="tabular-nums">R$ 100,00</span>, o produtor recebe
                                <span class="font-medium tabular-nums text-gray-900 dark:text-white">{{ money(exampleNet) }}</span>.
                            </template>
                        </p>
                    </div>
                </div>

                <div class="flex items-center gap-3 border-t border-gray-100 px-5 py-3 dark:border-gray-700/50">
                    <button
                        type="submit"
                        :disabled="saving || !dirty || !!priceError"
                        class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 dark:focus-visible:ring-white dark:focus-visible:ring-offset-gray-800"
                    >
                        <svg v-if="saving" class="-ml-0.5 mr-1.5 inline h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Salvar
                    </button>
                    <span v-if="dirty" class="text-xs text-amber-600 dark:text-amber-400">Alterações não salvas</span>
                    <span v-else-if="savedAt" class="text-xs text-gray-500 dark:text-gray-400">Salvo às {{ savedAt }}</span>
                </div>
            </form>

            <p v-if="configured && !loading" class="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Os sellers são ativados um a um em
                <router-link :to="{ name: 'users' }" class="font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">Usuários</router-link>
                &gt; Configurações.
            </p>
        </div>

        <CrmBonusPurchases v-if="!loading" />
    </div>
</template>

<script>
import { useToast } from "vue-toastification";
import SvgLoader from "../../components/layouts/SvgLoader.vue";
import FormToggle from "../../components/forms/FormToggle.vue";
import CrmBonusPurchases from "../../components/services/CrmBonusPurchases.vue";

const MAX_PRICE_CENTS = 100000;
const EXAMPLE_COMMISSION_CENTS = 10000;

export default {
    components: { SvgLoader, FormToggle, CrmBonusPurchases },
    setup() {
        return { toast: useToast() };
    },
    data() {
        return {
            loading: false,
            saving: false,
            configured: false,
            priceInput: "0,00",
            isActive: false,
            saved: { price: 0, isActive: false },
            savedAt: null,
        };
    },
    computed: {
        priceCents() {
            const normalized = String(this.priceInput).trim().replace(/\./g, "").replace(",", ".");

            if (!/^\d+(\.\d{0,2})?$/.test(normalized)) {
                return null;
            }

            return Math.round(Number(normalized) * 100);
        },
        priceError() {
            if (this.priceCents === null) {
                return "Use só números, como 2,00.";
            }

            if (this.priceCents > MAX_PRICE_CENTS) {
                return "O preço por venda vai até R$ 1.000,00.";
            }

            return null;
        },
        exampleNet() {
            return Math.max(EXAMPLE_COMMISSION_CENTS - this.priceCents, 0);
        },
        dirty() {
            return this.priceCents !== this.saved.price || this.isActive !== this.saved.isActive;
        },
        status() {
            if (!this.saved.isActive) {
                return {
                    label: "Desligado",
                    classes: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
                    dot: "bg-gray-400",
                };
            }

            if (this.saved.price === 0) {
                return {
                    label: "Registrando sem cobrar",
                    classes: "bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300",
                    dot: "bg-amber-500",
                };
            }

            return {
                label: `Cobrando ${this.money(this.saved.price)} por venda`,
                classes: "bg-green-100 text-green-800 dark:bg-green-500/10 dark:text-green-300",
                dot: "bg-green-500",
            };
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        money(cents) {
            return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        },
        fill(data) {
            this.configured = data.configured;
            this.isActive = data.is_active;
            this.priceInput = (data.price / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
            this.saved = { price: data.price, isActive: data.is_active };
        },
        normalizePrice() {
            if (this.priceCents !== null) {
                this.priceInput = (this.priceCents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
            }
        },
        fetch() {
            this.loading = true;
            axios
                .get("/api/services/crm-bonus")
                .then(({ data }) => this.fill(data))
                .catch(() => this.toast.error("Não foi possível carregar o CRM Bonus. Recarregue a página."))
                .finally(() => (this.loading = false));
        },
        submit() {
            if (this.priceError || !this.dirty) {
                return;
            }

            this.saving = true;
            axios
                .put("/api/services/crm-bonus", { price: this.priceCents, is_active: this.isActive })
                .then(({ data }) => {
                    this.fill(data);
                    this.savedAt = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                    this.toast.success("Cobrança do CRM Bonus salva.");
                })
                .catch(({ response }) => {
                    const errors = response?.data?.errors;
                    this.toast.error(
                        (Array.isArray(errors) && errors[0]) || response?.data?.message || "Não foi possível salvar. Tente de novo.",
                    );
                })
                .finally(() => (this.saving = false));
        },
    },
};
</script>
